import { MOBILE_MAX_WIDTH } from '~/constants/device';
import { isPublicPath } from '~/constants/publicPaths';

/** 代理ログイン（CH）中にアクセスできない画面パス */
const CH_PROXY_LOGIN_RESTRICTED_PATHS = [
  '/station/notification',
  '/station/delay',
];

/** スマートフォンでアクセスできない管理系画面のパスプレフィックス */
const MOBILE_RESTRICTED_PATH_PREFIXES = [
  '/account/approval',
  '/account/list',
  '/account/detail',
  '/account/edit',
  '/business/output-login-card',
];

/** 現在の表示幅がスマートフォン幅かどうかを返す */
const isMobileViewport = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.innerWidth <= MOBILE_MAX_WIDTH;
};

/** 管理者導線から参照するアカウント申請詳細画面かどうかを返す */
const isAdminApplicationDetailPath = (path: string) => {
  return path.startsWith('/account/application/detail') && !path.endsWith('/me');
};

/** スマートフォンでアクセスを禁止するパスかどうかを返す */
const isMobileRestrictedPath = (path: string) => {
  return MOBILE_RESTRICTED_PATH_PREFIXES.some((restrictedPath) => path.startsWith(restrictedPath))
    || isAdminApplicationDetailPath(path);
};

export default defineNuxtRouteMiddleware(async (to, from) => {
  const { loginUser } = useLoginUserStore();
  const logger = useLogger('0-00');

  // 作業駅が設定されていない場合は認証不要な画面とホーム画面以外は遷移禁止
  if (!isPublicPath(to.fullPath) && to.fullPath !== '/') {
    if (!loginUser?.workingStation) {
      logger.warn('作業駅が設定されていない場合は認証不要な画面とホーム画面以外にはアクセスできません。');
      return navigateTo('/');
    }
  }

  // 代理ログイン（CH）中は駅からのお知らせ・列車運行状況画面へのアクセスを禁止する
  if (
    loginUser?.proxyLoginMode === 'CH'
    && CH_PROXY_LOGIN_RESTRICTED_PATHS.includes(to.path)
  ) {
    logger.warn('代理ログイン（CH）中は駅からのお知らせ・列車運行状況画面にはアクセスできません。');
    return navigateTo('/');
  }

  // スマートフォンでは管理系画面へのアクセスを禁止する
  if (isMobileViewport() && isMobileRestrictedPath(to.path)) {
    logger.warn('スマートフォンでは管理系画面にはアクセスできません。');
    return navigateTo('/');
  }

  // 画面リロードは無条件でOK
  const navigationTiming = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
  if (navigationTiming?.type === 'reload') {
    logger.info('画面リロードが検出されました。');
    return;
  }

  // 荷役作業系の画面にURL直打ちアクセスされた場合は強制的にホーム画面に遷移させる
  if (from.name === undefined && to.fullPath.includes('/work/')) {
    logger.warn('荷役作業系の画面への直接アクセスはできません。');
    return navigateTo('/');
  }

  // 代理ログイン中はユーザ変更申請画面へのアクセスを禁止する
  if (to.fullPath.startsWith('/account/application/change') && loginUser?.proxyLoginMode) {
    logger.warn('代理ログイン中はユーザ変更申請画面にはアクセスできません。');
    return navigateTo('/');
  }
});
